import React, { useState, useEffect } from 'react';
import { useNavigate, Link as RouteLink } from 'react-router-dom';
import {
  Box,
  VStack,
  HStack,
  Text,
  Alert,
  AlertIcon,
  AlertDescription,
  Divider,
  useColorModeValue,
  Container,
  Progress,
  useToast,
  Checkbox,
  RadioGroup,
  Radio,
  Stack,
} from '@chakra-ui/react';
import {
  FiUser,
  FiMail,
  FiLock,
  FiEye,
  FiEyeOff,
  FiUserPlus,
  FiCheck,
  FiX,
} from 'react-icons/fi';

import { Card, Button, FormField } from '@grcs/components';

import { useUserOperations } from '@grcs/hook/useDatabase';
import { useLocalStorage } from '@grcs/hook/useLocalStorage';
import { validateEmail } from '@grcs/utils/validation/validateEmail';
import { UserRole } from '@grcs/types/UserRole';
import { TABLE_NAMES } from '@grcs/utils/database/types';

interface SignUpFormData {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  agreeToTerms: boolean;
}

interface ValidationError {
  field: keyof SignUpFormData | 'general';
  message: string;
}

interface PasswordStrength {
  score: number;
  feedback: string[];
  color: 'red' | 'orange' | 'yellow' | 'green';
}

export const SignUpPageEnhanced: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const userOps = useUserOperations();
  const [user, setUser] = useLocalStorage('user', {});

  // Form state
  const [formData, setFormData] = useState<SignUpFormData>({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    role: UserRole.STUDENT,
    agreeToTerms: false,
  });

  // UI state
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [step, setStep] = useState(1); // Multi-step form
  const [passwordStrength, setPasswordStrength] = useState<PasswordStrength>({
    score: 0,
    feedback: [],
    color: 'red',
  });

  // Theme colors
  const bgGradient = useColorModeValue(
    'linear(to-br, green.50, blue.50, purple.50)',
    'linear(to-br, green.900, blue.900, gray.900)',
  );
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  // Redirect if already logged in
  useEffect(() => {
    if (user?.role === UserRole.STUDENT) {
      navigate('/student', { replace: true });
    } else if (
      user?.role === UserRole.TEACHER ||
      user?.role === UserRole.ADMIN
    ) {
      navigate('/teacher', { replace: true });
    }
  }, [user, navigate]);

  // Password strength calculation
  useEffect(() => {
    if (formData.password) {
      const strength = calculatePasswordStrength(formData.password);
      setPasswordStrength(strength);
    }
  }, [formData.password]);

  const calculatePasswordStrength = (password: string): PasswordStrength => {
    let score = 0;
    const feedback: string[] = [];

    if (password.length >= 8) score += 25;
    else feedback.push('At least 8 characters');

    if (/[a-z]/.test(password)) score += 25;
    else feedback.push('Include lowercase letters');

    if (/[A-Z]/.test(password)) score += 25;
    else feedback.push('Include uppercase letters');

    if (/[0-9]/.test(password)) score += 25;
    else feedback.push('Include numbers');

    const color: PasswordStrength['color'] =
      score >= 75
        ? 'green'
        : score >= 50
        ? 'yellow'
        : score >= 25
        ? 'orange'
        : 'red';

    return { score, feedback, color };
  };

  // Form validation
  const validateForm = (currentStep: number): boolean => {
    const newErrors: ValidationError[] = [];

    if (currentStep >= 1) {
      // Step 1: Personal Information
      if (!formData.firstName.trim()) {
        newErrors.push({
          field: 'firstName',
          message: 'First name is required',
        });
      }
      if (!formData.lastName.trim()) {
        newErrors.push({ field: 'lastName', message: 'Last name is required' });
      }
      if (!formData.email) {
        newErrors.push({ field: 'email', message: 'Email is required' });
      } else {
        const emailError = validateEmail(formData.email);
        if (emailError) {
          newErrors.push({ field: 'email', message: emailError });
        }
      }
    }

    if (currentStep >= 2) {
      // Step 2: Account Security
      if (!formData.password) {
        newErrors.push({ field: 'password', message: 'Password is required' });
      } else if (formData.password.length < 8) {
        newErrors.push({
          field: 'password',
          message: 'Password must be at least 8 characters',
        });
      }

      if (!formData.confirmPassword) {
        newErrors.push({
          field: 'confirmPassword',
          message: 'Please confirm your password',
        });
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.push({
          field: 'confirmPassword',
          message: 'Passwords do not match',
        });
      }
    }

    if (currentStep >= 3) {
      // Step 3: Final Details
      if (!formData.agreeToTerms) {
        newErrors.push({
          field: 'agreeToTerms',
          message: 'You must agree to the terms and conditions',
        });
      }
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleInputChange = (field: keyof SignUpFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear field-specific errors
    setErrors((prev) => prev.filter((error) => error.field !== field));
  };

  const handleNextStep = () => {
    if (validateForm(step)) {
      setStep((prev) => Math.min(prev + 1, 3));
    }
  };

  const handlePrevStep = () => {
    setStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSignUp = async () => {
    if (!validateForm(3)) return;

    setIsLoading(true);
    setErrors([]);

    try {
      // Create auth account
      const { data, error } = await userOps.signUp(
        formData.email,
        formData.password,
      );

      if (error) {
        setErrors([{ field: 'general', message: error.message }]);
        return;
      }

      const { user: authUser } = data;
      if (!authUser) {
        setErrors([{ field: 'general', message: 'Failed to create account' }]);
        return;
      }

      // Create user profile
      const { data: userTableData } = await userOps.insertData(
        TABLE_NAMES.USER,
        {
          first_name: formData.firstName,
          last_name: formData.lastName,
          full_name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          auth_uid: authUser.id,
          role: formData.role,
          created_at: new Date(),
        },
      );

      if (userTableData && userTableData.length > 0) {
        setUser({
          ...userTableData[0],
          role: formData.role,
          isLogin: true,
          error: '',
          email: formData.email,
        });

        toast({
          title: 'Account created successfully!',
          description: `Welcome to GRCS, ${formData.firstName}!`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });

        // Navigate based on role
        if (formData.role === UserRole.STUDENT) {
          navigate('/student', { replace: true });
        } else {
          navigate('/teacher', { replace: true });
        }
      } else {
        setErrors([
          { field: 'general', message: 'Failed to create user profile' },
        ]);
      }
    } catch (error: any) {
      console.error('Sign up error:', error);
      setErrors([
        {
          field: 'general',
          message:
            error.message || 'An unexpected error occurred. Please try again.',
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const getFieldError = (field: string) => {
    return errors.find((error) => error.field === field)?.message;
  };

  const generalError = errors.find((error) => error.field === 'general')
    ?.message;

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <VStack spacing={4} align="stretch">
            <Text fontSize="lg" fontWeight="semibold" textAlign="center">
              Personal Information
            </Text>

            <FormField
              label="First Name"
              required
              error={getFieldError('firstName')}
            >
              <Box position="relative">
                <input
                  type="text"
                  placeholder="Enter your first name"
                  value={formData.firstName}
                  onChange={(e) =>
                    handleInputChange('firstName', e.target.value)
                  }
                  style={{
                    paddingLeft: '40px',
                    width: '100%',
                    padding: '12px',
                    border: `1px solid ${
                      getFieldError('firstName') ? 'red' : '#e2e8f0'
                    }`,
                    borderRadius: '6px',
                    fontSize: '16px',
                  }}
                />
                <Box
                  position="absolute"
                  left="12px"
                  top="50%"
                  transform="translateY(-50%)"
                  color="gray.400"
                >
                  <FiUser />
                </Box>
              </Box>
            </FormField>

            <FormField
              label="Last Name"
              required
              error={getFieldError('lastName')}
            >
              <Box position="relative">
                <input
                  type="text"
                  placeholder="Enter your last name"
                  value={formData.lastName}
                  onChange={(e) =>
                    handleInputChange('lastName', e.target.value)
                  }
                  style={{
                    paddingLeft: '40px',
                    width: '100%',
                    padding: '12px',
                    border: `1px solid ${
                      getFieldError('lastName') ? 'red' : '#e2e8f0'
                    }`,
                    borderRadius: '6px',
                    fontSize: '16px',
                  }}
                />
                <Box
                  position="absolute"
                  left="12px"
                  top="50%"
                  transform="translateY(-50%)"
                  color="gray.400"
                >
                  <FiUser />
                </Box>
              </Box>
            </FormField>

            <FormField
              label="Email Address"
              required
              error={getFieldError('email')}
            >
              <Box position="relative">
                <input
                  type="email"
                  placeholder="Enter your email address"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  style={{
                    paddingLeft: '40px',
                    width: '100%',
                    padding: '12px',
                    border: `1px solid ${
                      getFieldError('email') ? 'red' : '#e2e8f0'
                    }`,
                    borderRadius: '6px',
                    fontSize: '16px',
                  }}
                />
                <Box
                  position="absolute"
                  left="12px"
                  top="50%"
                  transform="translateY(-50%)"
                  color="gray.400"
                >
                  <FiMail />
                </Box>
              </Box>
            </FormField>
          </VStack>
        );

      case 2:
        return (
          <VStack spacing={4} align="stretch">
            <Text fontSize="lg" fontWeight="semibold" textAlign="center">
              Account Security
            </Text>

            <FormField
              label="Password"
              required
              error={getFieldError('password')}
            >
              <VStack spacing={2} align="stretch">
                <Box position="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Create a strong password"
                    value={formData.password}
                    onChange={(e) =>
                      handleInputChange('password', e.target.value)
                    }
                    style={{
                      paddingLeft: '40px',
                      paddingRight: '40px',
                      width: '100%',
                      padding: '12px',
                      border: `1px solid ${
                        getFieldError('password') ? 'red' : '#e2e8f0'
                      }`,
                      borderRadius: '6px',
                      fontSize: '16px',
                    }}
                  />
                  <Box
                    position="absolute"
                    left="12px"
                    top="50%"
                    transform="translateY(-50%)"
                    color="gray.400"
                  >
                    <FiLock />
                  </Box>
                  <Box
                    position="absolute"
                    right="12px"
                    top="50%"
                    transform="translateY(-50%)"
                    cursor="pointer"
                    color="gray.400"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </Box>
                </Box>

                {formData.password && (
                  <Box>
                    <HStack justify="space-between" mb={1}>
                      <Text fontSize="sm" color="gray.600">
                        Password Strength
                      </Text>
                      <Text
                        fontSize="sm"
                        color={`${passwordStrength.color}.500`}
                      >
                        {passwordStrength.score >= 75
                          ? 'Strong'
                          : passwordStrength.score >= 50
                          ? 'Good'
                          : passwordStrength.score >= 25
                          ? 'Fair'
                          : 'Weak'}
                      </Text>
                    </HStack>
                    <Progress
                      value={passwordStrength.score}
                      size="sm"
                      colorScheme={passwordStrength.color}
                      borderRadius="md"
                    />
                    {passwordStrength.feedback.length > 0 && (
                      <VStack align="start" mt={2} spacing={1}>
                        {passwordStrength.feedback.map((tip, index) => (
                          <HStack key={index} spacing={2}>
                            <FiX size={12} color="red" />
                            <Text fontSize="xs" color="gray.500">
                              {tip}
                            </Text>
                          </HStack>
                        ))}
                      </VStack>
                    )}
                  </Box>
                )}
              </VStack>
            </FormField>

            <FormField
              label="Confirm Password"
              required
              error={getFieldError('confirmPassword')}
            >
              <Box position="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm your password"
                  value={formData.confirmPassword}
                  onChange={(e) =>
                    handleInputChange('confirmPassword', e.target.value)
                  }
                  style={{
                    paddingLeft: '40px',
                    paddingRight: '40px',
                    width: '100%',
                    padding: '12px',
                    border: `1px solid ${
                      getFieldError('confirmPassword') ? 'red' : '#e2e8f0'
                    }`,
                    borderRadius: '6px',
                    fontSize: '16px',
                  }}
                />
                <Box
                  position="absolute"
                  left="12px"
                  top="50%"
                  transform="translateY(-50%)"
                  color="gray.400"
                >
                  <FiLock />
                </Box>
                <Box
                  position="absolute"
                  right="12px"
                  top="50%"
                  transform="translateY(-50%)"
                  cursor="pointer"
                  color="gray.400"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                </Box>
                {formData.confirmPassword &&
                  formData.password === formData.confirmPassword && (
                    <Box
                      position="absolute"
                      right="45px"
                      top="50%"
                      transform="translateY(-50%)"
                      color="green.500"
                    >
                      <FiCheck />
                    </Box>
                  )}
              </Box>
            </FormField>
          </VStack>
        );

      case 3:
        return (
          <VStack spacing={4} align="stretch">
            <Text fontSize="lg" fontWeight="semibold" textAlign="center">
              Account Type & Final Details
            </Text>

            <FormField label="Account Type" required>
              <RadioGroup
                value={formData.role}
                onChange={(value) =>
                  handleInputChange('role', value as UserRole)
                }
              >
                <Stack spacing={3}>
                  <Radio value={UserRole.STUDENT} colorScheme="blue">
                    <VStack align="start" spacing={1} ml={2}>
                      <Text fontWeight="medium">Student</Text>
                      <Text fontSize="sm" color="gray.500">
                        Access homework assignments and track your learning
                        progress
                      </Text>
                    </VStack>
                  </Radio>
                  <Radio value={UserRole.TEACHER} colorScheme="green">
                    <VStack align="start" spacing={1} ml={2}>
                      <Text fontWeight="medium">Teacher</Text>
                      <Text fontSize="sm" color="gray.500">
                        Manage classes, assign homework, and monitor student
                        performance
                      </Text>
                    </VStack>
                  </Radio>
                </Stack>
              </RadioGroup>
            </FormField>

            <Divider />

            <VStack spacing={3} align="stretch">
              <Checkbox
                isChecked={formData.agreeToTerms}
                onChange={(e) =>
                  handleInputChange('agreeToTerms', e.target.checked)
                }
                colorScheme="blue"
                size="sm"
              >
                <Text fontSize="sm">
                  I agree to the{' '}
                  <Text as="span" color="blue.500" textDecoration="underline">
                    Terms of Service
                  </Text>{' '}
                  and{' '}
                  <Text as="span" color="blue.500" textDecoration="underline">
                    Privacy Policy
                  </Text>
                </Text>
              </Checkbox>

              {getFieldError('agreeToTerms') && (
                <Text color="red.500" fontSize="sm">
                  {getFieldError('agreeToTerms')}
                </Text>
              )}
            </VStack>
          </VStack>
        );

      default:
        return null;
    }
  };

  return (
    <Box
      minH="100vh"
      bgGradient={bgGradient}
      display="flex"
      alignItems="center"
    >
      <Container maxW="2xl" py={12}>
        <VStack spacing={8} align="stretch">
          {/* Header */}
          <VStack spacing={4} textAlign="center">
            <Box>
              <FiUserPlus size={48} color="green" />
            </Box>
            <VStack spacing={2}>
              <Text fontSize="2xl" fontWeight="bold">
                Join GRCS
              </Text>
              <Text color="gray.600">
                Create your account to start your Chinese learning journey
              </Text>
            </VStack>
          </VStack>

          {/* Progress Indicator */}
          <VStack spacing={2}>
            <HStack justify="space-between" width="100%">
              <Text fontSize="sm" color="gray.600">
                Step {step} of 3
              </Text>
              <Text fontSize="sm" color="gray.600">
                {Math.round((step / 3) * 100)}% Complete
              </Text>
            </HStack>
            <Progress
              value={(step / 3) * 100}
              size="sm"
              colorScheme="green"
              borderRadius="full"
            />
          </VStack>

          {/* Sign Up Card */}
          <Card
            bg={cardBg}
            borderColor={borderColor}
            shadow="2xl"
            borderRadius="2xl"
          >
            <VStack spacing={6} align="stretch">
              {/* General Error */}
              {generalError && (
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  <AlertDescription>{generalError}</AlertDescription>
                </Alert>
              )}

              {/* Step Content */}
              {renderStepContent()}

              {/* Navigation Buttons */}
              <HStack justify="space-between">
                <Button
                  variant="secondary"
                  onClick={handlePrevStep}
                  isDisabled={step === 1}
                >
                  Previous
                </Button>

                {step < 3 ? (
                  <Button onClick={handleNextStep}>Next</Button>
                ) : (
                  <Button
                    leftIcon={<FiUserPlus />}
                    onClick={handleSignUp}
                    isLoading={isLoading}
                    loadingText="Creating Account..."
                    colorScheme="green"
                  >
                    Create Account
                  </Button>
                )}
              </HStack>

              <Divider />

              {/* Footer Links */}
              <VStack spacing={3} fontSize="sm" textAlign="center">
                <Text color="gray.500">
                  Already have an account?{' '}
                  <RouteLink to="/auth/admin-login">
                    <Text
                      as="span"
                      color="blue.500"
                      _hover={{ color: 'blue.600' }}
                    >
                      Sign In
                    </Text>
                  </RouteLink>
                </Text>
              </VStack>
            </VStack>
          </Card>

          {/* Footer */}
          <Text fontSize="xs" color="gray.500" textAlign="center">
            By creating an account, you're joining a community of learners
            dedicated to mastering Chinese reading comprehension.
          </Text>
        </VStack>
      </Container>
    </Box>
  );
};
